import React, { useEffect, useState } from 'react';
import { Modal, Spin, Alert, Button } from 'antd';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// 配置 PDF.js 的 worker 文件（版本号带上避免缓存旧版本）
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.5.4.296.js`;

export default function PdfPreviewModal({ fileName, fileUrl, onCancel, open = true }) {
  const [numPages, setNumPages] = useState(null);
  const [pdfWidth, setPdfWidth] = useState(600);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfSrc, setPdfSrc] = useState(null);

  // 拉取 PDF 为 blob，避免 content-disposition 和 CORS 干扰
  useEffect(() => {
    let revokedUrl = null;
    const loadPdf = async () => {
      if (!fileUrl) return;
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(fileUrl, { credentials: 'include', mode: 'cors' });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const blob = await res.blob();
        revokedUrl = URL.createObjectURL(blob);
        setPdfSrc(revokedUrl);
      } catch (err) {
        console.error('[PdfPreviewModal] 获取 PDF 失败', err);
        setError(err?.message || '加载失败');
      } finally {
        setLoading(false);
      }
    };
    loadPdf();
    return () => {
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
    };
  }, [fileUrl]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (err) => {
    setLoading(false);
    console.error('[PdfPreviewModal] PDF 渲染失败', err);
    setError(err?.message || '渲染失败');
  };

  const onPageLoadSuccess = ({ width }) => {
    setPdfWidth(width);
  };

  const handleDownload = () => {
    if (!fileUrl) return;
    const link = document.createElement('a');
    link.href = fileUrl;
    const name = fileName || '文件';
    link.download = name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      title={`【${fileName || 'PDF'}】预览`}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={pdfWidth + 100}
      style={{ top: 20 }}
    >
      {error ? (
        <Alert message={`加载 PDF 文件失败：${error}`} type="error" showIcon />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <Button size="small" type="primary" onClick={handleDownload} disabled={!fileUrl}>
              下载
            </Button>
          </div>
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
              <Spin size="large" />
            </div>
          )}
          {fileUrl && (
            <div style={{ height: '88vh', overflowY: 'auto', padding: '24px' }}>
              <Document
                file={pdfSrc || fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={null}
                error={null}
              >
                {Array.from(new Array(numPages || 0), (_, index) => (
                  <Page key={`page_${index + 1}`} pageNumber={index + 1} onLoadSuccess={onPageLoadSuccess} />
                ))}
              </Document>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
